import { useCallback, useEffect } from "react";
import { API } from "../../../services/api";
import { unregisterSave } from "../../save/pendingSave";
import { loadDraftBackup } from "../../save/draftBackup";
import { adaptCommandeStatusToContext, buildCommandeDetailOptimisticPatch, buildCommandeListPatch, ensureDraftClientKeys, fromBackendToDraft } from "../model/commandeDraft";
import { usableDraftBackupForCommande } from "../model/commandeDraftMerge";

const NO_CACHE_HEADERS = { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" };

// Ouverture d'une commande : la vue détail relit toujours l'état réellement persisté (un cache navigateur ou proxy
// pouvait renvoyer la réponse antérieure au PATCH). Le brouillon est relu depuis le serveur (ou la mémoire de
// l'onglet), jamais depuis le navigateur.
export function useCommandeDetailLoader({ selectedCommande, selectedCommandeRef, d, list, context }) {
    const { setCommandeDetail, setDraft, draftRef, draftRevisionRef, originalDraftRef, restoredBackupRef,
        patientIdentityConflictRef, setPatientIdentityConflict, setIsDirty, setSaveErrors, setAutoSaveStatus } = d;
    const { optimisticListPatchesRef } = list;
    useEffect(() => {
        if (!selectedCommande) return;
        const requestedCommandeId = selectedCommande.id;
        let cancelled = false;
        const stillWanted = () => !cancelled && selectedCommandeRef.current?.id === requestedCommandeId;
        patientIdentityConflictRef.current = null;
        setPatientIdentityConflict(null);
        setCommandeDetail(null);
        setDraft(null);
        originalDraftRef.current = null;
        API.get(`/commandes/${requestedCommandeId}`, { params: { _ts: Date.now() }, headers: NO_CACHE_HEADERS })
            .then(async res => {
                if (!stillWanted()) return;
                const storedBackup = await loadDraftBackup(res.data.id);
                if (!stillWanted()) return;
                const optimisticPatch = buildCommandeDetailOptimisticPatch(
                    optimisticListPatchesRef.current.get(res.data.id)
                );
                const patchedCommande = adaptCommandeStatusToContext({ ...res.data, ...optimisticPatch }, context);
                const backup = usableDraftBackupForCommande(patchedCommande, storedBackup);
                const d = ensureDraftClientKeys(backup?.draft || fromBackendToDraft(patchedCommande));
                restoredBackupRef.current = !!backup?.draft;
                setCommandeDetail(patchedCommande);
                setDraft(d);
                draftRef.current = d;
                draftRevisionRef.current = 0;
                originalDraftRef.current = fromBackendToDraft(patchedCommande);
                setIsDirty(!!backup?.draft);
                setSaveErrors([]);
                setAutoSaveStatus(backup?.draft ? "local" : "idle");
            })
            .catch(error => {
                if (!cancelled) console.error(error);
            });

        return () => {
            cancelled = true;
        };
    // Relu seulement quand la commande ouverte change, comme avant le découpage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCommande, context]);
}

// Navigation entre la liste et le détail, et changements faits sur la commande ouverte (EHPAD, lu, favori).
export function useCommandeNavigation({ d, list, saving, selection }) {
    const { draftRef, isDirtyRef, setDraft, setCommandeDetail, commandeDetail, rememberDraftForList, clearDraft } = d;
    const { setCommandes, applyListPatches, markJustReleased, optimisticListPatchesRef, refresh } = list;
    const { selectedCommande, setSelectedCommande, setView, setSavedToast, setPreviewCommande, setShowCreateModal } = selection;

    const handleCommandeUpdated = useCallback((newCommande) => {
        setCommandeDetail(newCommande);
        setDraft(prev => prev ? { ...prev, ehpadId: newCommande.ehpad?.id ?? null } : prev);
        const patch = buildCommandeListPatch(newCommande);
        if (Object.keys(patch).length > 0) {
            setCommandes(prev => prev.map(cmd =>
                cmd.id === newCommande.id ? { ...cmd, ...patch } : cmd
            ));
        }
    }, [setCommandeDetail, setCommandes, setDraft]);

    const handleFlagChange = useCallback((commandeId, changes) => {
        const existingPatch = optimisticListPatchesRef.current.get(commandeId) || {};
        optimisticListPatchesRef.current.set(commandeId, { ...existingPatch, ...changes });
        setCommandes(prev => prev.map(cmd => cmd.id === commandeId ? { ...cmd, ...changes } : cmd));
        setPreviewCommande(prev => prev?.id === commandeId ? { ...prev, ...changes } : prev);
        setCommandeDetail(prev => prev?.id === commandeId ? { ...prev, ...changes } : prev);
        if (draftRef.current?.id === commandeId) {
            const nextDraft = { ...draftRef.current, ...changes };
            draftRef.current = nextDraft;
            setDraft(nextDraft);
        }
    }, [draftRef, optimisticListPatchesRef, setCommandeDetail, setCommandes, setDraft, setPreviewCommande]);

    const handleFavoriteChange = useCallback(async (commandeId, favorite) => {
        const previousDetailFavorite = commandeDetail?.id === commandeId ? commandeDetail.favorite : undefined;
        const previousDraftFavorite = draftRef.current?.id === commandeId ? draftRef.current.favorite : undefined;

        handleFlagChange(commandeId, { favorite });

        try {
            await API.patch(`/commandes/${commandeId}/favorite`, { favorite });
        } catch (e) {
            const fallback = previousDetailFavorite ?? previousDraftFavorite;
            if (fallback !== undefined) handleFlagChange(commandeId, { favorite: fallback });
            throw e;
        }
    }, [commandeDetail, draftRef, handleFlagChange]);

    const handleCommandeCreated = useCallback((newCommande) => {
        setShowCreateModal(false);
        refresh();
        setSelectedCommande({ id: newCommande.id });
        setCommandeDetail(newCommande);
        setDraft(fromBackendToDraft(newCommande));
        setView("detail");
    }, [refresh, setCommandeDetail, setDraft, setSelectedCommande, setShowCreateModal, setView]);

    // Mise à jour optimiste : le verrou est retiré de la liste avant que sa libération ne soit confirmée.
    const releaseInList = useCallback((id) => {
        markJustReleased(id);
        setCommandes(prev => applyListPatches(prev.map(c => c.id === id ? { ...c, lockedBy: null } : c)));
    }, [applyListPatches, markJustReleased, setCommandes]);

    const handleBack = async () => {
        // La ref est synchronisée dès la saisie, contrairement au state React
        // qui peut encore être en retard lors d'un clic immédiat sur Retour.
        const wasdirty = isDirtyRef.current;
        const closingId = selectedCommande?.id;
        const saved = await saving.saveDraft();
        if (!saved && wasdirty) {
            rememberDraftForList(draftRef.current);
            return;
        }
        unregisterSave();
        setView("list");
        setSelectedCommande(null);
        clearDraft();
        if (closingId) releaseInList(closingId);
        refresh();
        if (saved && wasdirty) {
            setSavedToast(true);
            setTimeout(() => setSavedToast(false), 3000);
        }
    };

    const goToList = useCallback(() => {
        if (isDirtyRef.current) rememberDraftForList(draftRef.current);
        unregisterSave();
        setView("list");
        setSelectedCommande(prev => {
            if (prev?.id) releaseInList(prev.id);
            return null;
        });
        clearDraft();
    }, [clearDraft, draftRef, isDirtyRef, releaseInList, rememberDraftForList, setSelectedCommande, setView]);

    return { handleCommandeUpdated, handleFlagChange, handleFavoriteChange, handleCommandeCreated, handleBack, goToList };
}
